import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { Creator, FormProviders, FormRequest, FormRequestTemplate, FormStatus, Person } from '@typings/api-models';
import ListChecks from '@components/Icons/ListChecks';
import ListBullets from '@components/Icons/ListBullets';
import CheckCircle from '@components/Icons/CheckCircle';
import Buttons from '@components/Buttons';
import { useUser } from '@hooks';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import s from './FormRequestCard.module.scss';

type Props = {
  creator: Pick<Creator, 'slug'>;
  request: Pick<FormRequest, 'id' | 'url' | 'status'> & {
    template: Pick<FormRequestTemplate, 'id' | 'name' | 'provider'>;
    person: Pick<Person, 'id'>;
  };
  canClick: boolean;
  setIsFulfilled?: (b: boolean) => void;
};

const FormRequestCard: React.FC<Props> = ({ setIsFulfilled, canClick, creator, request }) => {
  const [user] = useUser();
  const router = useRouter();

  const isCompleted = request.status === FormStatus.COMPLETED;

  useEffect(() => {
    setIsFulfilled?.(isCompleted);
  }, [isCompleted, setIsFulfilled]);

  const handleClick = (): void => {
    if (!user) {
      return;
    }
    if (request.person.id === user.id && !isCompleted && request.template.provider !== FormProviders.Anvil) {
      window.open(request.url, '_blank');
    } else {
      router.push(getUrl(IDENTIFIERS.FORM_REQUEST_DETAILS, { creatorSlug: creator.slug, formRequestId: request.id }));
    }
  };

  return (
    <Buttons className={s.container} isFullWidth isWrapper key={request.id} onClick={handleClick} disabled={!canClick}>
      <figure style={(isCompleted && { backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }) || {}}>
        {isCompleted ? <ListChecks size={24} color="var(--color-gray-0)" /> : <ListBullets size={24} color="var(--color-gray-100)" />}
        {isCompleted && (
          <div className={s.complete}>
            <CheckCircle size={16} weight="fill" color="var(--color-success)" />
          </div>
        )}
      </figure>
      <div>
        <p className='caption'>Form request</p>
        <p className="body-sm-bold">{request.template.name}</p>
      </div>
      {canClick && <Buttons isExtraSmall isThird className={s.open}>View</Buttons>}
    </Buttons>
  );
};

export default FormRequestCard;
